import * as React from 'react';
import { Text, Link, Dropdown, Option, InfoLabel, Input, TableCellLayout, TableColumnDefinition, Textarea, Subtitle1, ToolbarButton, createTableColumn, makeStyles, Checkbox, Field, OptionOnSelectData, InputOnChangeData, CheckboxOnChangeData, Tooltip, OptionGroup, TableRowId } from '@fluentui/react-components';
import {
    ArrowCircleLeft24Regular,
    Save24Regular,
    Add24Regular,
    Delete24Regular,
    PresenceAvailableRegular,
    PresenceBlockedRegular
} from "@fluentui/react-icons";
import { CustomProperty, Product, ProductComponentType, ProductVersion, PropertyType, RequirementLevel } from '../../dto/Product';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { GenericToolbar } from '../../components/GenericToolbar';
import { formStyle, formInputStyle, flexRow, flexRowWrap, flexColumn, formInputInlineStyle, infoLabel } from '../../styles/GlobalStyles';
import { useGlobalDispatch } from '../../state/GlobalStateProvider';
import { GenericGrid } from '../../components/GenericGrid';
import { CopyButton } from '../../components/CopyButton';
import { FormBase } from './FormBase';
import { v4 as uuidv4 } from 'uuid';
import { fetchRecord, submit } from '../../util/FormUtils';
import { capitalizeFirstChar } from '../../util/StringUtils';
import { LifeTime, LifeTimeKind, LifeTimeMode, LifeTimeUnit, RelativeLifeTime } from '../../dto/License';
import { addToUtcDate } from '../../util/DateUtils';
import { FluentDialog } from '../../components/FluentDialog';
import { IDatabaseRecord } from '../../dto/IDatabaseRecord';
import { TriStateBoolDropdown } from '../../components/TriStateBoolDropdown';

const entitySet = "products";

const rowSelector = (item: ProductVersion) => item.guid;

const useStyles = makeStyles({
    formInput: formInputStyle,
    formInputInline: formInputInlineStyle,
    form: formStyle,
    flexRow: flexRow,
    flexColumn: flexColumn,
    infoLabel: infoLabel,
    flexRowWrap: flexRowWrap,
    grow: {
        flex: 1
    }
});

const defaultRecord: Partial<Product> = {};

export const ProductForm: React.FC<any> = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const { id, publisherId } = useParams();

    const globalDispatch = useGlobalDispatch();
    const [isSaving, setIsSaving] = React.useState(false);
    const [record, setRecord] = React.useState<Product>({} as Product);
    const isExisting = !!record?.guid && record.guid !== "new";
    const isDisabled = isExisting && record.state === "inactive";
    const isUserAllowedToSave = React.useMemo(() => (!record.guid || ["contributor", "admin"].includes(record.currentUserAccessLevel)), [record]);
    const isEditable = React.useMemo(() => !isDisabled && isUserAllowedToSave, [isDisabled, isUserAllowedToSave]);
    const areRequiredFieldsMissing = React.useMemo(() => !record.name || !record.componentTypes?.length, [record.componentTypes?.length, record.name]);
    const isSaveDisabled = React.useMemo(() => isSaving || areRequiredFieldsMissing || !isEditable, [isSaving, areRequiredFieldsMissing, isEditable]);

    const [selectedCustomProperties, setSelectedCustomProperties] = React.useState<TableRowId[]>([]);
    const [isAddingCustomProperty, setIsAddingCustomProperty] = React.useState(false);
    const [customPropertyId, setCustomPropertyId] = React.useState("");
    const [customPropertyName, setCustomPropertyName] = React.useState("");
    const [customPropertyDisplayName, setCustomPropertyDisplayName] = React.useState("");
    const [customPropertyInternalDefaultValue, setCustomPropertyInternalDefaultValue] = React.useState<string | number | boolean | null>(null);
    const [customPropertyExternalDefaultValue, setCustomPropertyExternalDefaultValue] =  React.useState<string | number | boolean | null>(null);
    const [customPropertyType, setCustomPropertyType] = React.useState<PropertyType | undefined>();
    const [customPropertyRequirementLevel, setCustomPropertyRequirementLevel] = React.useState<RequirementLevel | undefined>();

    const styles = useStyles();

    const versionColumns: TableColumnDefinition<ProductVersion>[] = React.useMemo(() => [
        createTableColumn<ProductVersion>({
            columnId: "VersionName",
            compare: (a, b) => {
                return a.name.localeCompare(b.name);
            },
            renderHeaderCell: () => {
                return "Version Name";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout truncate>
                        <InfoLabel
                            info={
                                <Text>
                                    Associated ID: {item.guid}
                                    <br />
                                    <br />
                                    This ID will show up as "kid" on generated licenses.
                                    Each product version mirrors a different private key, so licenses will only be valid when being validated using the product version's public key that was used for generating. You can setup client-side validation to accept multiple public keys to enhance this.
                                    The public key associated to this version is publicly available <Link href={`/api/public/products/${record.externalId}/.well-known/jwks.json`} target="_blank">here</Link> so that you can easily verify license integrity on pages like <Link href="https://jwt.io" target="_blank">jwt.io</Link> (jwt.io fetches the public key automatically).
                                </Text>
                            }
                        >
                            {item.name}
                        </InfoLabel>
                    </TableCellLayout>
                );
            }
        }),
        createTableColumn<ProductVersion>({
            columnId: "PublicKey",
            compare: (a, b) => {
                return a.name.localeCompare(b.publicKey);
            },
            renderHeaderCell: () => {
                return "Public Key";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout truncate>
                        <div style={{ display: "flex"}}>
                            <Textarea disabled={true} value={item.publicKey} style={{ flex: 1 }} />
                            <CopyButton value={item.publicKey} />
                        </div>
                    </TableCellLayout>
                );
            },
        })
    ], [record.externalId]);

    const customPropertyColumns: TableColumnDefinition<CustomProperty>[] = React.useMemo(() => [
        createTableColumn<CustomProperty>({
            columnId: "Name",
            compare: (a, b) => {
                return a.name.localeCompare(b.name);
            },
            renderHeaderCell: () => {
                return "Name";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout truncate>
                        {item.name}
                    </TableCellLayout>
                );
            }
        }),
        createTableColumn<CustomProperty>({
            columnId: "DisplayName",
            compare: (a, b) => {
                return a.displayName.localeCompare(b.displayName);
            },
            renderHeaderCell: () => {
                return "Display Name";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout truncate>
                        {item.displayName}
                    </TableCellLayout>
                );
            }
        }),
        createTableColumn<CustomProperty>({
            columnId: "PropertyType",
            compare: (a, b) => {
                return a.propertyType.localeCompare(b.propertyType);
            },
            renderHeaderCell: () => {
                return "Property Type";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout truncate>
                        {item.propertyType}
                    </TableCellLayout>
                );
            }
        }),
        createTableColumn<CustomProperty>({
            columnId: "RequirementLevel",
            compare: (a, b) => {
                return a.requirementLevel.localeCompare(b.requirementLevel);
            },
            renderHeaderCell: () => {
                return "Requirement Level";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout truncate>
                        {item.requirementLevel}
                    </TableCellLayout>
                );
            }
        })
    ], []);

    const onLifeTimeChange = React.useCallback((event: any, data: OptionOnSelectData) =>
    {
        const kind = data.optionValue! as LifeTimeKind;

        let newLifeTime: LifeTime;

        if (kind === "relative")
        {
            newLifeTime = {
                kind: "relative",
                unit: "month",
                mode: "simple",
                value: 1
            };
        }
        else if(kind === "absolute")
        {
            newLifeTime = {
                kind: "absolute",
                mode: "end",
                date: addToUtcDate(new Date(Date.now()), { months: 1}).toISOString().split('T')[0]
            };
        }
        else
        {
            newLifeTime = {
                kind: "unlimited"
            };
        }

        setRecord((record) => ({...record, lifeTime: newLifeTime}));
    }, []);

    const onLifeTimeModeChange = React.useCallback((event: any, data: OptionOnSelectData) =>
    {
        const mode = data.optionValue! as LifeTimeMode;

        setRecord((record) =>
        {
            if (record.trialSettings?.lifeTime?.kind === "relative")
            {
                return {
                    ...record,
                    lifeTime: {
                        kind: "relative",
                        unit: record.trialSettings?.lifeTime?.unit,
                        value: record.trialSettings?.lifeTime?.value,
                        mode: mode,
                        time: record.trialSettings?.lifeTime?.time
                    }
                };
            }
            else if (record.trialSettings?.lifeTime?.kind === "absolute")
            {
                return {
                    ...record,
                    lifeTime: {
                        kind: "absolute",
                        date: record.trialSettings?.lifeTime?.date,
                        mode: mode,
                        time: record.trialSettings?.lifeTime?.time
                    }
                };
            }
            else
            {
                return record;
            }
        });
    }, []);

    const onLifeTimeUnitChange = React.useCallback((event: any, data: OptionOnSelectData) =>
    {
        const unit = data.optionValue! as LifeTimeUnit;

        setRecord((record) => (
            {
                ...record,
                trialSettings:
                {
                    lifeTime:
                    {
                        kind: "relative",
                        unit: unit,
                        mode: (record.trialSettings?.lifeTime?.kind === "absolute" || record.trialSettings?.lifeTime?.kind === "relative") ? record.trialSettings.lifeTime.mode : undefined,
                        value: record.trialSettings?.lifeTime?.kind === "relative" ? record.trialSettings?.lifeTime.value ?? 1 : 1
                    }
                }
            }
        ));
    }, []);

    const onLifeTimeValueChange = React.useCallback((ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) =>
    {
        setRecord((record) => (
            {
                ...record,
                trialSettings:
                {
                    ...(record.trialSettings ?? {}),
                    lifeTime:
                    {
                        kind: "relative",
                        unit: record.trialSettings?.lifeTime?.kind === "relative" ? record.trialSettings.lifeTime.unit : undefined,
                        mode: (record.trialSettings?.lifeTime?.kind === "absolute" || record.trialSettings?.lifeTime?.kind === "relative") ? record.trialSettings.lifeTime.mode : undefined,
                        value: parseInt(data.value)
                    }
                }
            }
        ))
    }, []);

    const onNameChange = React.useCallback((ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) =>
    {
        setRecord((record) => ({...record, name: data.value}));
    }, []);

    const onTelemetryEnabledChange = React.useCallback((ev: React.ChangeEvent<HTMLInputElement>, data: CheckboxOnChangeData) =>
    {
        setRecord((record) => ({
            ...record,
            telemetryEnabled: data.checked as boolean
        }));
    }, []);

    const onTrialEnabledChange = React.useCallback((ev: React.ChangeEvent<HTMLInputElement>, data: CheckboxOnChangeData) =>
    {
        setRecord((record) => ({
            ...record,
            trialEnabled: data.checked as boolean,
            trialSettings: data.checked
                ? { lifeTime: { kind: "relative", unit: "week", mode: "simple", value: 2 } }
                : undefined
        }));
    }, []);

    const cancelDialog = React.useCallback(() =>
    {
        setCustomPropertyId("");
        setCustomPropertyName("");
        setCustomPropertyDisplayName("");
        setCustomPropertyType(undefined);
        setCustomPropertyRequirementLevel(undefined);
        setCustomPropertyInternalDefaultValue(null);
        setCustomPropertyExternalDefaultValue(null);
        setIsAddingCustomProperty(false);  
    }, []);

    const submitDialog = React.useCallback(() =>
    {
        if (customPropertyId)
        {
            setRecord({
                ...record,
                customProperties: [
                    ...(record.customProperties ?? []).map(c => c.guid === customPropertyId ? ({
                        ...c, 
                        name: customPropertyName, 
                        displayName: customPropertyDisplayName, 
                        propertyType: customPropertyType as PropertyType, 
                        requirementLevel: customPropertyRequirementLevel as RequirementLevel,
                        defaultValues: (customPropertyInternalDefaultValue === null && customPropertyExternalDefaultValue === null)
                            ? undefined
                            : ({
                                internal: customPropertyInternalDefaultValue !== null 
                                    ? customPropertyInternalDefaultValue 
                                    : undefined,
                                external: customPropertyExternalDefaultValue !== null
                                    ? customPropertyExternalDefaultValue 
                                    : undefined,
                            })
                    } as CustomProperty) : c)
                ]
            });
        }
        else {
            const newCustomProperty: CustomProperty = {
                guid: uuidv4(),
                name: customPropertyName,
                displayName: customPropertyDisplayName,
                propertyType: customPropertyType as PropertyType,
                requirementLevel: customPropertyRequirementLevel as RequirementLevel,
                defaultValues: (customPropertyInternalDefaultValue === null && customPropertyExternalDefaultValue === null)
                    ? undefined
                    : ({
                        internal: customPropertyInternalDefaultValue !== null 
                            ? customPropertyInternalDefaultValue 
                            : undefined,
                        external: customPropertyExternalDefaultValue !== null
                            ? customPropertyExternalDefaultValue 
                            : undefined,
                    })
            };
            setRecord({ ...record, customProperties: [...(record.customProperties ?? []), newCustomProperty] });
        }
        cancelDialog();
    }, [customPropertyId, cancelDialog, record, customPropertyName, customPropertyDisplayName, customPropertyType, customPropertyRequirementLevel, customPropertyInternalDefaultValue, customPropertyExternalDefaultValue]);

    const editCustomProperty = React.useCallback((propertyId: TableRowId) => {
        const customProperty = record.customProperties.find(c => c.guid === propertyId.toString());

        if (customProperty) {
            setCustomPropertyId(customProperty.guid);
            setCustomPropertyName(customProperty.name);
            setCustomPropertyDisplayName(customProperty.displayName);
            setCustomPropertyType(customProperty.propertyType);
            setCustomPropertyRequirementLevel(customProperty.requirementLevel);
            setCustomPropertyInternalDefaultValue(
                customProperty.defaultValues?.internal ?? null
            );
            setCustomPropertyExternalDefaultValue(
                customProperty.defaultValues?.external ?? null
            );

            setIsAddingCustomProperty(true);
        }
    }, [record.customProperties]);

    const onCustomPropertySelection = React.useCallback((selection: TableRowId[]) =>
    {
        setSelectedCustomProperties(selection);
    }, []);

    const onCustomPropertyDeletion = React.useCallback(() =>
    {
        setRecord({
            ...record,
            customProperties: [
                ...(record.customProperties ?? []).filter(e => !selectedCustomProperties.includes(e.guid))]
        });
    }, [record, selectedCustomProperties]);

    const onCustomPropertyTypeChange = React.useCallback((event: any, data: OptionOnSelectData) =>
    {
        const newType = data.optionValue! as PropertyType;

        setCustomPropertyInternalDefaultValue(null);
        setCustomPropertyExternalDefaultValue(null);

        setCustomPropertyType(newType);
    }, []);

    const onCustomPropertyRequirementLevelChange = React.useCallback((event: any, data: OptionOnSelectData) =>
    {
        setCustomPropertyRequirementLevel(data.optionValue! as RequirementLevel);
    }, []);

    const onCustomPropertyNameChange = React.useCallback((ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) =>
    {
        setCustomPropertyName(data.value);
    }, []);

    const onCustomPropertyDisplayNameChange = React.useCallback((ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) =>
    {
        setCustomPropertyDisplayName(data.value);
    }, []);

    const onCustomPropertyInternalDefaultValueChange = React.useCallback((ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) =>
    {
        if (data.value === "")
        {
            setCustomPropertyInternalDefaultValue(null);
        }
        else if(customPropertyType === "number")
        {
            const parsedValue = parseFloat(data.value);
            const numericValue = data.value == null || data.value === "" || isNaN(parsedValue)
                ? null
                : parsedValue;

            setCustomPropertyInternalDefaultValue(numericValue);
        }
        else
        {
            setCustomPropertyInternalDefaultValue(data.value);
        }
    }, [customPropertyType]);

    const onCustomPropertyExternalDefaultValueChange = React.useCallback((ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) =>
    {
        if (data.value === "")
        {
            setCustomPropertyExternalDefaultValue(null);
        }
        else if(customPropertyType === "number")
        {
            const parsedValue = parseFloat(data.value);
            const numericValue = data.value == null || data.value === "" || isNaN(parsedValue)
                ? null
                : parsedValue;

            setCustomPropertyExternalDefaultValue(numericValue);
        }
        else
        {
            setCustomPropertyExternalDefaultValue(data.value);
        }
    }, [customPropertyType]);

    const onBack = React.useCallback(() =>
    {
        navigate(`/workspace/publishers/${publisherId}/${entitySet}`);
    }, [navigate, publisherId]);

    const onSubmit = React.useCallback(async () =>
    {
        if (!isSaveDisabled)
        {
            await submit(publisherId, id, record, entitySet, setRecord, setIsSaving, navigate, globalDispatch);
        }
    }, [globalDispatch, id, isSaveDisabled, navigate, publisherId, record]);

    const onStateToggle = React.useCallback(async () =>
    {
        if (!isDisabled)
        {
            const update = ({...record, state: "inactive"} as IDatabaseRecord);
            await submit(publisherId, id, update, entitySet, setRecord, setIsSaving, navigate, globalDispatch);
        }
        else
        {
            const update = ({...record, state: "active"} as IDatabaseRecord);
            await submit(publisherId, id, update, entitySet, setRecord, setIsSaving, navigate, globalDispatch);
        }
    }, [globalDispatch, id, isDisabled, navigate, publisherId, record]);

    const didInitRef = React.useRef<string | null>(null);

    // This is the initialization hook
    React.useEffect(() =>
    {
        // If we already initialized once, just exit, updates supply us with the latest record state automatically
        if (didInitRef.current !== id)
        {
            if (!id)
            {
                return;
            }

            // This happens right after the transition from id === "new" to the actual id
            if (location?.state?.record?.guid === id)
            {
                // Use record from location once...
                setRecord(location.state.record);

                //...then discard so that it can never overwrite local edits
                navigate(location.pathname, {
                    replace: true,
                    state: null
                });
            }
            else
            {
                // In all other cases, just fetch
                fetchRecord(
                    publisherId,
                    id,
                    entitySet,
                    setRecord,
                    globalDispatch,
                    defaultRecord as Product
                );
            }

            didInitRef.current = id;
        }
    },
    [
        id,
        publisherId,
        location.pathname,
        location.state,
        navigate,
        globalDispatch
    ]);

    const dialogs = React.useMemo(() => (
        <>
            <FluentDialog title={`${ customPropertyId ? "Edit" : "Add"} Custom Property`} open={isAddingCustomProperty} submitDisabled={!customPropertyName || !customPropertyDisplayName || !customPropertyType || !customPropertyRequirementLevel || !isEditable} onSubmit={submitDialog} onCancel={cancelDialog}>
                <div className={styles.form}>
                    <Field
                        label="Name"
                        hint={`${(customPropertyName ?? "").length} / 200`}
                        required
                    >
                        <Input maxLength={200} type="text" value={customPropertyName ?? ""} onChange={onCustomPropertyNameChange} />
                    </Field>

                    <Field
                        label="Display Name"
                        hint={`${(customPropertyDisplayName ?? "").length} / 200`}
                        required
                    >
                        <Input maxLength={200} type="text" value={customPropertyDisplayName ?? ""} onChange={onCustomPropertyDisplayNameChange} />
                    </Field>

                    <Field
                        label="Type"
                        required
                    >
                        <Dropdown
                            placeholder="Choose a type"
                            value={capitalizeFirstChar(customPropertyType) ?? ""}
                            selectedOptions={customPropertyType ? [customPropertyType] : []}
                            onOptionSelect={onCustomPropertyTypeChange}
                        >
                            <Option key="string" value="string">String</Option>
                            <Option key="bool" value="bool">Bool</Option>
                            <Option key="number" value="number">Number</Option>
                        </Dropdown>
                    </Field>

                    <Field
                        label="Requirement Level"
                        required
                    >
                        <Dropdown
                            placeholder="Choose a requirement level"
                            value={capitalizeFirstChar(customPropertyRequirementLevel) ?? ""}
                            selectedOptions={customPropertyRequirementLevel ? [customPropertyRequirementLevel] : []}
                            onOptionSelect={onCustomPropertyRequirementLevelChange}
                        >
                            <Option key="none" value="none">None</Option>
                            <Option key="recommended" value="recommended">Recommended</Option>
                            <Option key="required" value="required">Required</Option>
                        </Dropdown>
                    </Field>

                    { customPropertyType && (
                        <Field
                            label="Internal default value"
                            hint={customPropertyType === "string" ? `${(customPropertyInternalDefaultValue?.toString() ?? "").length} / 200` : undefined}
                        >
                        {
                            (["bool"] as Array<PropertyType>).includes(customPropertyType) ? (
                                <TriStateBoolDropdown
                                    value={customPropertyInternalDefaultValue as boolean | null}
                                    onChange={setCustomPropertyInternalDefaultValue}
                                    placeholder="Choose a default value"
                                />
                            )
                            : (
                                <Input
                                    type="text"
                                    value={customPropertyInternalDefaultValue?.toString() ?? ""}
                                    onChange={onCustomPropertyInternalDefaultValueChange}
                                    placeholder="Insert value..."
                                />
                            )
                        }
                        </Field>
                    )}

                    { customPropertyType && (
                        <Field
                            label="External default value"
                            hint={customPropertyType === "string" ? `${(customPropertyExternalDefaultValue?.toString() ?? "").length} / 200` : undefined}
                        >
                        {
                            (["bool"] as Array<PropertyType>).includes(customPropertyType) ? (
                                <TriStateBoolDropdown
                                    value={customPropertyExternalDefaultValue as boolean | null}
                                    onChange={setCustomPropertyExternalDefaultValue}
                                    placeholder="Choose a default value"
                                />
                            )
                            : (
                                <Input
                                    type="text"
                                    value={customPropertyExternalDefaultValue?.toString() ?? ""}
                                    onChange={onCustomPropertyExternalDefaultValueChange}
                                    placeholder="Insert value..."
                                />
                            )
                        }
                        </Field>
                    )}
                </div>
            </FluentDialog>
        </>
    ), [cancelDialog, customPropertyDisplayName, customPropertyId, customPropertyName, customPropertyRequirementLevel, customPropertyType, customPropertyInternalDefaultValue, customPropertyExternalDefaultValue, isAddingCustomProperty, isEditable, onCustomPropertyDisplayNameChange, onCustomPropertyNameChange, onCustomPropertyRequirementLevelChange, onCustomPropertyTypeChange, onCustomPropertyInternalDefaultValueChange, onCustomPropertyExternalDefaultValueChange, styles.form, submitDialog]);

    const toolbar = React.useMemo(() => (
        <GenericToolbar
            actionButtons={
                <>
                    <Tooltip relationship='label' content="Back">
                        <ToolbarButton
                            aria-label="Back"
                            appearance="subtle"
                            icon={<ArrowCircleLeft24Regular />}
                            onClick={onBack}
                        />
                    </Tooltip>
                    <Tooltip relationship='label' content="Save (Ctrl + S)">
                        <ToolbarButton
                            aria-label="Save"
                            appearance="primary"
                            icon={<Save24Regular />}
                            disabled={isSaveDisabled}
                            onClick={onSubmit}
                        />
                    </Tooltip>
                    <Tooltip relationship='label' content={ isDisabled ? "Enable" : "Disable"}>
                        <ToolbarButton
                            aria-label={ isDisabled ? "Enable" : "Disable"}
                            appearance="primary"
                            disabled={!isExisting || isSaving || areRequiredFieldsMissing || !isUserAllowedToSave}
                            icon={ isDisabled ? <PresenceAvailableRegular /> : <PresenceBlockedRegular />}
                            onClick={onStateToggle}
                        />
                    </Tooltip>
                </>
            }
            farGroup={
                <>
                    <Field
                        label={
                            <InfoLabel size="medium" className={styles.infoLabel} info="External representation of this record's id. Visible on generated licenses.">
                                External Id
                            </InfoLabel>
                        }
                    >
                        <Input type="text" value={record.externalId ?? ""} disabled contentAfter={<CopyButton disabled={!record.externalId} value={record.externalId} />} />
                    </Field>
                </>
            }
        />
    ), [onBack, isSaveDisabled, onSubmit, isDisabled, isExisting, isSaving, areRequiredFieldsMissing, isUserAllowedToSave, onStateToggle, styles.infoLabel, record.externalId]);

    const modeField = React.useMemo(() => {
        if (record?.trialSettings?.lifeTime?.kind === "absolute" || record?.trialSettings?.lifeTime?.kind === "relative")
        {
            return (
                <Field
                    className={styles.grow}
                    label={
                        <InfoLabel
                            required
                            info={
                                <Text>
                                    For trial licenses, currently only mode 'simple' is supported.

                                    <ul>
                                        <li><b>Simple</b>: Takes the same time that we currently have when saving</li>
                                    </ul>
                                </Text>
                            }
                        >
                            Mode
                        </InfoLabel>
                    }
                >
                    <Dropdown
                        disabled={true}
                        placeholder="Choose a mode for the lifetime"
                        value={capitalizeFirstChar(record?.trialSettings?.lifeTime?.mode) ?? ""}
                        selectedOptions={record?.trialSettings?.lifeTime?.mode ? [record.trialSettings.lifeTime.mode] : []}
                        onOptionSelect={onLifeTimeModeChange}
                    >
                        <Option key="simple" value="simple">Simple</Option>
                        <Option key="start" value="start">Start</Option>
                        <Option key="end" value="end">End</Option>
                        <Option key="exact" value="exact">Exact</Option>
                    </Dropdown>
                </Field>
            )
        }
        else
        {
            return undefined;
        }
    }, [onLifeTimeModeChange, record.trialSettings?.lifeTime, styles.grow]);

    const onComponentTypesChange = React.useCallback((event: any, data: OptionOnSelectData) => {
        setRecord((record) => ({
            ...record,
            componentTypes: data.selectedOptions.map(o => o as ProductComponentType) ?? []
        }));
    }, []);

    const body = React.useMemo(() => (
        <>
            <Field
                label={
                    <InfoLabel size="medium" required className={styles.infoLabel} info="Product name. Visible on generated licenses.">
                        Name
                    </InfoLabel>
                }
                hint={`${(record.name ?? "").length} / 200`}
            >
                <Input maxLength={200} type="text" value={record.name ?? ""} disabled={!isEditable} onChange={onNameChange} />
            </Field>

            <Field
                label={
                    <InfoLabel size="medium" required className={styles.infoLabel} info="Define which components your product includes. This is important for knowing which environment identifiers are needed inside licenses.">
                        Components
                    </InfoLabel>
                }
            >
                <Dropdown
                    placeholder="Choose one or multiple components your product consists of"
                    disabled={!isEditable}
                    multiselect={true}
                    value={record?.componentTypes?.map(c => capitalizeFirstChar(c)).join(", ") ?? ""}
                    selectedOptions={record?.componentTypes?.map(c => c) ?? []}
                    onOptionSelect={onComponentTypesChange}
                >
                    <OptionGroup label="Microsoft Power Platform">
                        <Option key="pcf" value="pcf">Pcf</Option>
                        <Option key="plugin" value="plugin">Plugin</Option>
                        <Option key="canvas" value="canvas app">Canvas app</Option>
                        <Option key="flow" value="flow">Flow</Option>
                    </OptionGroup>
                </Dropdown>
            </Field>

            <Field
                label={
                    <InfoLabel size="medium" className={styles.infoLabel} info="Enable telemetry to allow reporting of usage insights from customer organizations back to Ianus Guard portal">
                        Telemetry Enabled
                    </InfoLabel>
                }
            >
                <Checkbox checked={record.telemetryEnabled ?? false} disabled={!isEditable} onChange={onTelemetryEnabledChange} />
            </Field>

            { record?.telemetryEnabled && (
                <>
                    <Field
                        label={
                            <InfoLabel size="medium" className={styles.infoLabel} info="Primary telemetry key for sending usage insights from customer organizations to Ianus Guard portal.">
                                Primary Telemetry Key
                            </InfoLabel>
                        }
                        hint={`Save this key in a secure place after first generation, you will only be able to see the full key once!`}
                    >
                        <Input type="text" value={record.telemetryKeys?.find(k => k.slot === "primary")?.telemetryKeyDisplay ?? "Save to generate"} disabled contentAfter={<CopyButton disabled={!record.telemetryKeys?.find(k => k.slot === "primary")?.telemetryKeyDisplay} value={record.telemetryKeys?.find(k => k.slot === "primary")?.telemetryKeyDisplay} />} />
                    </Field>

                    <Field
                        label={
                            <InfoLabel size="medium" className={styles.infoLabel} info="Secondary telemetry key for sending usage insights from customer organizations to Ianus Guard portal. Can be used for rotating.">
                                Secondary Telemetry Key
                            </InfoLabel>
                        }
                        hint={`Save this key in a secure place after first generation, you will only be able to see the full key once!`}
                    >
                        <Input type="text" value={record.telemetryKeys?.find(k => k.slot === "secondary")?.telemetryKeyDisplay ?? "Save to generate"} disabled contentAfter={<CopyButton disabled={!record.telemetryKeys?.find(k => k.slot === "secondary")?.telemetryKeyDisplay} value={record.telemetryKeys?.find(k => k.slot === "secondary")?.telemetryKeyDisplay} />} />
                    </Field>
                </>
            ) }

            <Field
                label={
                    <InfoLabel size="medium" className={styles.infoLabel} info="If 'Trial Enabled' and 'Landing Page Enabled' (publisher setting) are both checked, end customers will be able to create trial licenses for this product on your landing page. (Only available in Enterprise tier)">
                        Trial Enabled
                    </InfoLabel>
                }
            >
                <Checkbox checked={record.trialEnabled ?? false} disabled={!isEditable} onChange={onTrialEnabledChange} />
            </Field>

            { record?.trialEnabled && (
                <div className={styles.flexRowWrap}>
                    <Field
                        className={styles.grow}
                        label={
                            <InfoLabel required size="medium" className={styles.infoLabel} info="This defines how the expiry will be calculated. For trial licenses, currently only 'relative' is supported.">
                                License Lifetime Kind
                            </InfoLabel>
                        }
                    >
                        <Dropdown
                            disabled={true}
                            placeholder="Choose a lifetime for the license"
                            value={capitalizeFirstChar(record?.trialSettings?.lifeTime?.kind) ?? ""}
                            selectedOptions={record?.trialSettings?.lifeTime?.kind ? [record.trialSettings.lifeTime.kind] : []}
                            onOptionSelect={onLifeTimeChange}
                        >
                            <Option key="relative" value="relative">Relative</Option>
                            <Option key="absolute" value="absolute">Absolute</Option>
                            <Option key="unlimited" value="unlimited">Unlimited</Option>
                        </Dropdown>
                    </Field>

                    <Field
                        className={styles.grow}
                        label="Unit"
                        required
                    >
                        <Dropdown
                            placeholder="Choose a unit"
                            disabled={!isEditable}
                            value={capitalizeFirstChar((record?.trialSettings?.lifeTime as RelativeLifeTime)?.unit) ?? ""}
                            selectedOptions={(record?.trialSettings?.lifeTime as RelativeLifeTime)?.unit ? [(record.trialSettings!.lifeTime as RelativeLifeTime).unit!] : []}
                            onOptionSelect={onLifeTimeUnitChange}
                        >
                            <Option key="day" value="day">Day</Option>
                            <Option key="week" value="week">Week</Option>
                            <Option key="month" value="month">Month</Option>
                            <Option key="year" value="year">Year</Option>
                        </Dropdown>
                    </Field>

                    <Field
                        className={styles.grow}
                        label="Value"
                        required
                    >
                        <Input
                            type="number"
                            step={1}
                            min={1}
                            disabled={!isEditable}
                            value={(record?.trialSettings?.lifeTime as RelativeLifeTime)?.value?.toString() ?? ""}
                            onChange={onLifeTimeValueChange}
                            placeholder="Select a value for the associated unit..."
                        />
                    </Field>

                    { modeField }
                </div>
            )}

            { !!record.guid &&
                <div style={{ borderRadius: "5px", width: "100%", height: "100%", marginTop: "20px" }}>
                    <InfoLabel size="medium" className={styles.infoLabel} info="Product versions add the possibility of defining multiple private and public keys for issuance and validation of your licenses. Right now Ianus Guard portal supports only the managed key pair that is automatically created on creation of each product record.">
                        <Subtitle1>
                            Product Versions
                        </Subtitle1>
                    </InfoLabel>

                    <GenericGrid
                        onRowClick={() => {}}
                        columns={versionColumns}
                        items={record?.versions ?? []}
                        rowSelector={rowSelector}
                        actionButtons={
                            <>
                                <ToolbarButton
                                    aria-label="Add new version"
                                    appearance="primary"
                                    icon={<Add24Regular />}
                                    disabled={true || !isEditable}
                                    onClick={() => { }}
                                />
                                <ToolbarButton
                                    aria-label="Delete record"
                                    disabled={true || !isEditable}
                                    icon={<Delete24Regular />}
                                />
                            </>
                        }
                    />
                </div>
            }

            <div style={{ borderRadius: "5px", marginTop: "20px" }}>
                <InfoLabel size="medium" className={styles.infoLabel} info="Define your own properties that can be added to licenses. They will become part of the license content and available to your products.">
                    <Subtitle1>
                        Custom Properties
                    </Subtitle1>
                </InfoLabel>

                <GenericGrid
                    onRowClick={editCustomProperty}
                    onSelectionChange={onCustomPropertySelection}
                    columns={customPropertyColumns}
                    items={record.customProperties ?? []}
                    rowSelector={rowSelector}
                    actionButtons={
                        <>
                            <ToolbarButton
                                aria-label="Add new record"
                                appearance="primary"
                                icon={<Add24Regular />}
                                disabled={!isEditable}
                                onClick={() => setIsAddingCustomProperty(true)}
                            />
                            <ToolbarButton
                                aria-label="Delete record"
                                icon={<Delete24Regular />}
                                disabled={!isEditable}
                                onClick={onCustomPropertyDeletion}
                            />
                        </>
                    }
                />
            </div>
        </>
    ), [styles.infoLabel, styles.flexRowWrap, styles.grow, record.name, record?.componentTypes, record.telemetryEnabled, record.telemetryKeys, record.trialEnabled, record.trialSettings, record.guid, record?.versions, record.customProperties, isEditable, onNameChange, onComponentTypesChange, onTelemetryEnabledChange, onTrialEnabledChange, onLifeTimeChange, onLifeTimeUnitChange, onLifeTimeValueChange, modeField, versionColumns, editCustomProperty, onCustomPropertySelection, customPropertyColumns, onCustomPropertyDeletion]);

    return (
        <FormBase
            id={id}
            record={record}
            isSaving={isSaving}
            dialogs={dialogs}
            toolbar={toolbar}
            body={body}
            onSave={onSubmit}
        />
    );
};